import { Dynamo, MedicalDataTypes } from '../../../primary-record-types';
import React, { NamedExoticComponent } from 'react';
import { View } from 'react-native';
import useMedicalProfile from '../../../hooks/api/useMedicalProfile';
import { useIsMobileLayout } from '../../../hooks/useIsMobileLayout';
import { useProfileCounts } from '../../../hooks/useProfileCounts';
import {
    ProfilesTabParamList,
    useActiveTabActiveScreen,
    useProfilesTabNavigation,
} from '../../../utils/appNavigationNames';
import { getAgeString } from '../../../utils/getDateString';
import { getIntl } from '../../../utils/intl';
import urls from '../../../utils/urls';
import { LoadableAvatar } from '../../v2BaseComponents/Avatar';
import { usePermittedDomains } from '../../v2BaseComponents/Permissions/hooks';
import SkeletonLoadingContainer from '../../v2BaseComponents/SkeletonLoadingContainer';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import SideTabNavigation from '../../v2BaseComponents/TabView/SideTabNavigation';
import { SideTabContentWidthVariant } from '../../v2BaseComponents/TabView/utils';
import { SideDrawerContainer } from '../../v2BaseComponents/SideDrawer';
import ProfileSubscribeBanner from './ProfileSubscribeBanner';
import { useMedicalProfileDirectives } from '../../../hooks/api/useMedicalProfileDirectives';
import { useAppType } from '../../../hooks/useAppType';
import { codeStatusTypeMessages } from '../../../utils/sharedMessages';
import SearchBar from '../SearchBar';

const intl = getIntl('ProfileSideTabNavigation', {
    condition: 'Conditions',
    medication: 'Medications',
    allergy: 'Allergies',
    vaccination: 'Vaccinations',
    procedure: 'Procedures',
    lab: 'Labs',
    imaging: 'Imaging',
    visit: 'Visits',
    careTeam: 'Care Team',
    reports: 'Reports',
    documents: 'Files',
    profileInfo: 'Profile Info',
    connectToPortal: 'Portals',
    access: 'Access',
    notes: 'Notes',
    aiAssistant: 'Chat Assistant',
    healthOverview: 'Health Records',
    resources: 'Resources',
    dashboard: 'Dashboard',
    home: 'Home',
    messages: 'Messages',
    collaborators: 'Collaborators',
});

export const ProfileSideTabContext = React.createContext<React.ReactElement | null>(null);

export type ProfileSideTabNavigationOwnProps = {
    children: any;
};

export type ProfileSideTabNavigationProps = ProfileSideTabNavigationOwnProps;

export const MaybeProfileSideTabNavigation: React.FC<ProfileSideTabNavigationProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const sideTab = React.useContext(ProfileSideTabContext);

    if (!sideTab || isMobile) return props.children;

    return (
        <View
            style={{
                flexDirection: 'row',
                height: '100%',
                width: '100%',
            }}
        >
            {sideTab}
            <View style={{ flex: 1, height: '100%' }}>{props.children}</View>
        </View>
    );
};

export const wrapInProfileSideTabNavigation = (Component: NamedExoticComponent<any>): any =>
    React.memo((props: any) => (
        <MaybeProfileSideTabNavigation>
            <Component {...props} />
        </MaybeProfileSideTabNavigation>
    ));

export type ProfileSideTab =
    | 'activity'
    | MedicalDataTypes.PrimaryRecordResourceCategory
    | 'care-team'
    | 'reports'
    | 'documents'
    | 'profile-info'
    | 'connect-to-portal'
    | 'access'
    | 'notes'
    | 'chat'
    | 'messages';

const noteEntityTypeToSideTab = (entityType: Dynamo.NoteEntityType): ProfileSideTab | null => {
    switch (entityType) {
        case 'medical-profile':
            return 'notes';
        case 'care-team-member':
        case 'care-team':
            return 'care-team';
        case 'allergy':
        case 'allergy-resource':
            return 'allergy';
        case 'condition':
        case 'condition-resource':
            return 'condition';
        case 'medication':
        case 'medication-resource':
            return 'medication';
        case 'visit':
        case 'visit-resource':
            return 'visit';
        case 'procedure':
        case 'procedure-resource':
            return 'procedure';
        case 'vaccination':
        case 'vaccination-resource':
            return 'vaccination';
        case 'lab':
        case 'lab-resource':
            return 'lab';
        default:
            return null;
    }
};

const getProfileSideTab = <TScreen extends keyof ProfilesTabParamList>(
    screen: TScreen | null,
    params: ProfilesTabParamList[TScreen] | undefined,
): ProfileSideTab | null => {
    switch (screen) {
        case 'root':
            return null;
        case 'add':
            return null;
        case 'invite':
            return null;
        case 'edit':
            return 'profile-info';
        case 'contact-details':
            return 'profile-info';
        case 'emergency-contacts':
            return 'profile-info';
        case 'edit-emergency-contact':
            return 'profile-info';
        case 'create-emergency-contact':
            return 'profile-info';
        case 'care-team':
            return 'care-team';
        case 'add-care-team':
            return 'care-team';
        case 'edit-care-team-member':
            return 'care-team';
        case 'care-team-member':
            return 'care-team';
        case 'copy-care-team-member':
            return 'care-team';
        case 'add-related-care-team-member':
            return 'care-team';
        case 'invite-collaborator':
            return 'access';
        case 'invite-details':
            return 'access';
        case 'collaborator':
            return 'access';
        case 'add-transfer-request':
            return 'access';
        case 'transfer-request':
            return 'access';
        case 'transfer-request-response':
            return 'access';
        case 'add-card':
            return 'profile-info';
        case 'edit-card':
            return 'profile-info';
        case 'data':
            return (params as ProfilesTabParamList['data'])?.category || null;
        case 'data-bulk-update':
            return (params as ProfilesTabParamList['data-bulk-update'])?.category || null;
        case 'data-details':
            return (params as ProfilesTabParamList['data-details'])?.category || null;
        case 'add-data':
            return (params as ProfilesTabParamList['add-data'])?.category || null;
        case 'edit-data':
            return (params as ProfilesTabParamList['edit-data'])?.category || null;
        case 'add-portal':
            return 'connect-to-portal';
        case 'add-fasten-portal':
            return 'connect-to-portal';
        case 'add-tefca-connection':
            return 'connect-to-portal';
        case 'add-note':
            return noteEntityTypeToSideTab((params as ProfilesTabParamList['add-note']).entityType);
        case 'copy-note':
            return noteEntityTypeToSideTab((params as ProfilesTabParamList['copy-note']).noteEntityType);
        case 'edit-note':
            return noteEntityTypeToSideTab((params as ProfilesTabParamList['edit-note']).noteEntityType);
        case 'note':
            return noteEntityTypeToSideTab((params as ProfilesTabParamList['note']).noteEntityType);
        case 'transfer-note':
            return noteEntityTypeToSideTab((params as ProfilesTabParamList['transfer-note']).noteEntityType);
        case 'portal-connection':
            return 'connect-to-portal';
        case 'fasten-portal-connection':
            return 'connect-to-portal';
        case 'reports':
            return 'reports';
        case 'add-shared-report':
            return 'reports';
        case 'shared-report':
            return 'reports';
        case 'files':
            return 'documents';
        case 'file':
            return 'documents';
        case 'date-labs':
            return 'lab';
        case 'lab-history':
            return 'lab';
        case 'profile-activity':
            return 'activity';
        case 'profile-activity-details':
            return 'activity';
        case 'message-profile':
            return 'activity';
        case 'chat':
            return 'chat';
        case 'org-invite-accept-success':
            return 'access';
        case 'connect':
            return 'connect-to-portal';
        case 'access':
            return 'access';
        case 'profile-notes':
            return 'notes';
        case 'add-suggested-profile-folders':
            return 'documents';
        case 'edit-client-invitation':
            return null;
        case 'collaborator-invoice':
            return 'access';
        case 'edit-legal-decision-maker':
            return 'profile-info';
        case 'edit-code-status':
            return 'profile-info';
        case 'messages':
            return 'messages';
        case 'visit-invite-emails':
            return 'visit';
        default:
            return null;
    }
};

const routeContentWidthMap: Record<keyof ProfilesTabParamList, SideTabContentWidthVariant> = {
    root: 'xl',
    add: 'xl',
    invite: 'xl',
    edit: 'md',
    'contact-details': 'md',
    'emergency-contacts': 'md',
    'edit-emergency-contact': 'md',
    'create-emergency-contact': 'md',
    'care-team': 'md',
    'add-care-team': 'md',
    'edit-care-team-member': 'md',
    'care-team-member': 'md',
    'copy-care-team-member': 'md',
    'add-related-care-team-member': 'md',
    'invite-collaborator': 'md',
    'invite-details': 'xl',
    collaborator: 'md',
    'add-transfer-request': 'md',
    'transfer-request': 'md',
    'transfer-request-response': 'md',
    'add-card': 'md',
    'edit-card': 'md',
    data: 'md',
    'data-bulk-update': 'md',
    'data-details': 'md',
    'add-data': 'md',
    'edit-data': 'md',
    'add-portal': 'md',
    'add-fasten-portal': 'md',
    'add-tefca-connection': 'md',
    'add-note': 'md',
    'copy-note': 'md',
    'edit-note': 'md',
    note: 'md',
    'transfer-note': 'md',
    'portal-connection': 'md',
    'fasten-portal-connection': 'md',
    reports: 'md',
    'add-shared-report': 'md',
    'shared-report': 'md',
    files: 'md',
    file: 'md',
    'date-labs': 'md',
    'lab-history': 'md',
    'profile-activity': 'md',
    'profile-activity-details': 'md',
    'message-profile': 'md',
    chat: 'lg',
    'org-invite-accept-success': 'xl',
    connect: 'md',
    access: 'md',
    'profile-notes': 'md',
    'client-profile': 'xl',
    'edit-client-record': 'xl',
    'edit-client-profile-groups': 'xl',
    'client-record': 'xl',
    'add-client-record': 'xl',
    'add-client-record-invitation': 'xl',
    'add-client-record-note': 'xl',
    'add-client-invitation': 'xl',
    'client-record-note': 'xl',
    'edit-client-record-note': 'xl',
    'copy-org-care-team-member-to-clients': 'xl',
    'copy-org-note-to-clients': 'xl',
    'copy-visit-to-clients': 'xl',
    'send-clients-message': 'xl',
    'clients-activity': 'xl',
    'client-activity-details': 'xl',
    'client-activity-history': 'xl',
    profile: 'xl',
    'bulk-add-client-records': 'xl',
    'add-suggested-profile-folders': 'md',
    'edit-client-invitation': 'xl',
    'bulk-add-client-profiles-groups': 'xl',
    'edit-client-billing-info': 'xl',
    'client-invoice-items': 'xl',
    'collaborator-invoice': 'md',
    'edit-legal-decision-maker': 'md',
    'edit-code-status': 'md',
    'bulk-invite-clients': 'md',
    messages: 'md',
    'visit-invite-emails': 'md',
    'send-note': 'md',
    'message-preferences': 'md',
};

const routesToRenderSearch: (keyof ProfilesTabParamList)[] = ['profile', 'profile-activity'];

const ProfileSideTabHeader: React.FC<{ profileId: string }> = ({ profileId }) => {
    const { profile, isLoadingProfile } = useMedicalProfile({ medicalProfileId: profileId });
    const isProfessional = useAppType() === 'professional';
    const { isLoadingCodeStatus, codeStatus } = useMedicalProfileDirectives({ medicalProfileId: profileId });

    return (
        <Stack horizontal fullWidth space={4} style={{ marginBottom: 4 }}>
            <LoadableAvatar
                size={'md'}
                avatarUrl={urls.api.medicalProfiles.avatar(profileId)}
                firstName={profile?.contactDetails?.firstName || ''}
                preferredFirstName={profile?.contactDetails?.preferredFirstName || ''}
                lastName={profile?.contactDetails?.lastName || ''}
            />
            <Stack vertical flex={1} justify="space-between" align="flex-start" testID="profile-info-header">
                <Stack vertical>
                    <SkeletonLoadingContainer isLoading={isLoadingProfile}>
                        <Text variant="bold-h6">
                            {isLoadingProfile
                                ? 'placeholder'
                                : profile?.contactDetails?.preferredFirstName ||
                                  profile?.contactDetails?.firstName ||
                                  ''}
                        </Text>
                    </SkeletonLoadingContainer>
                    <SkeletonLoadingContainer isLoading={isLoadingProfile}>
                        <Text variant="bold-h6">
                            {isLoadingProfile ? 'placeholder-last' : profile?.contactDetails?.lastName || ''}
                        </Text>
                    </SkeletonLoadingContainer>
                </Stack>
                <SkeletonLoadingContainer isLoading={isLoadingProfile}>
                    <Text variant="caption-1">
                        {isLoadingProfile
                            ? '01/01/2000 (40)'
                            : profile?.contactDetails.dob
                            ? getAgeString(profile?.contactDetails.dob)
                            : ''}
                    </Text>
                </SkeletonLoadingContainer>
                {isProfessional && (codeStatus || isLoadingCodeStatus) && (
                    <SkeletonLoadingContainer isLoading={isLoadingCodeStatus}>
                        <Text variant="caption-1">
                            {codeStatus ? codeStatusTypeMessages(codeStatus.codeStatus) : 'Placeholder'}
                        </Text>
                    </SkeletonLoadingContainer>
                )}
            </Stack>
        </Stack>
    );
};

const ProfileSideTabNavigationWithProfileId: React.FC<ProfileSideTabNavigationProps & { profileId: string | null }> = (
    props,
) => {
    const permittedDomains = usePermittedDomains(
        props.profileId,
        [
            'visits',
            'medications',
            'conditions',
            'allergies',
            'imaging',
            'procedures',
            'vaccinations',
            'labs',
            'careTeam',
            'files',
            'reports',
            'patientPortals',
        ],
        'view',
    );

    const isMobile = useIsMobileLayout();
    const screen = useActiveTabActiveScreen();
    const contentWidth =
        screen?.activeScreen && screen.activeScreen in routeContentWidthMap
            ? (routeContentWidthMap as any)[screen.activeScreen] || undefined
            : undefined;
    const sideTab = getProfileSideTab((screen?.activeScreen || null) as any, screen?.params || {});
    const profileCounts = useProfileCounts({ profileId: sideTab ? props.profileId : null, emptyForNoAccess: true });
    const profilesTabNavigation = useProfilesTabNavigation();

    const onTabChange = (tab: ProfileSideTab) => {
        if (!props.profileId) {
            console.error('No profileId for tab ' + tab);
            return;
        }

        switch (tab) {
            case 'activity':
                profilesTabNavigation.navigate('profile-activity', { profileId: props.profileId });
                break;
            case 'medication':
                profilesTabNavigation.navigate('data', { profileId: props.profileId, category: 'medication' });
                break;
            case 'condition':
                profilesTabNavigation.navigate('data', { profileId: props.profileId, category: 'condition' });
                break;
            case 'visit':
                profilesTabNavigation.navigate('data', { profileId: props.profileId, category: 'visit' });
                break;
            case 'allergy':
                profilesTabNavigation.navigate('data', { profileId: props.profileId, category: 'allergy' });
                break;
            case 'procedure':
                profilesTabNavigation.navigate('data', { profileId: props.profileId, category: 'procedure' });
                break;
            case 'vaccination':
                profilesTabNavigation.navigate('data', { profileId: props.profileId, category: 'vaccination' });
                break;
            case 'lab':
                profilesTabNavigation.navigate('data', { profileId: props.profileId, category: 'lab' });
                break;
            case 'care-team':
                profilesTabNavigation.navigate('care-team', { profileId: props.profileId });
                break;
            case 'reports':
                profilesTabNavigation.navigate('reports', { profileId: props.profileId });
                break;
            case 'documents':
                profilesTabNavigation.navigate('files', { profileId: props.profileId });
                break;
            case 'profile-info':
                profilesTabNavigation.navigate('edit', { profileId: props.profileId, firstName: '' });
                break;
            case 'connect-to-portal':
                profilesTabNavigation.navigate('connect', { profileId: props.profileId });
                break;
            case 'access':
                profilesTabNavigation.navigate('access', { profileId: props.profileId });
                break;
            case 'notes':
                profilesTabNavigation.navigate('profile-notes', { profileId: props.profileId });
                break;
            case 'chat':
                profilesTabNavigation.navigate('chat', { profileId: props.profileId });
                break;
            case 'messages':
                profilesTabNavigation.navigate('messages', { profileId: props.profileId });
                break;
        }
    };

    const maybeGetContentHeaderRenderer = (): (() => React.ReactNode) | undefined => {
        if (
            (screen?.activeTab === 'profiles' || screen?.activeTab === 'clients') &&
            screen.activeScreen &&
            routesToRenderSearch.includes(screen.activeScreen as any) &&
            props.profileId
        ) {
            return () => (
                <View style={{ width: '100%', marginBottom: contentStackSpacing }}>
                    <SearchBar medicalProfileId={props.profileId!} />
                </View>
            );
        } else {
            return undefined;
        }
    };

    return (
        <SideDrawerContainer>
            <SideTabNavigation
                renderHeader={() => (props.profileId ? <ProfileSideTabHeader profileId={props.profileId} /> : null)}
                renderFooter={() => (props.profileId ? <ProfileSubscribeBanner profileId={props.profileId} /> : null)}
                tabs={[
                    {
                        title: intl('dashboard'),
                        options: [
                            {
                                title: intl('home'),
                                value: 'activity',
                                icon: 'Home',
                            },
                            {
                                title: intl('messages'),
                                value: 'messages',
                                icon: 'MessageSquare',
                            },
                            {
                                title: intl('notes'),
                                value: 'notes',
                                secondaryText: profileCounts.notes,
                                icon: 'NotesFilled',
                            },
                            {
                                title: intl('aiAssistant'),
                                value: 'chat',
                                icon: 'AISparkle',
                            },
                        ],
                    },
                    {
                        title: intl('healthOverview'),
                        options: [
                            {
                                title: intl('connectToPortal'),
                                value: 'connect-to-portal',
                                secondaryText: profileCounts.portals,
                                isDisabled: !permittedDomains.includes('patientPortals'),
                                icon: 'Portals',
                            },
                            {
                                title: intl('medication'),
                                value: 'medication',
                                secondaryText: profileCounts.medication,
                                isDisabled: !permittedDomains.includes('medications'),
                                icon: 'MedicationsFilled',
                            },
                            {
                                title: intl('condition'),
                                value: 'condition',
                                secondaryText: profileCounts.condition,
                                isDisabled: !permittedDomains.includes('conditions'),
                                icon: 'ConditionsFilled',
                            },
                            {
                                title: intl('allergy'),
                                value: 'allergy',
                                secondaryText: profileCounts.allergy,
                                isDisabled: !permittedDomains.includes('allergies'),
                                icon: 'AllergiesFilled',
                            },
                            {
                                title: intl('lab'),
                                value: 'lab',
                                secondaryText: profileCounts.lab,
                                isDisabled: !permittedDomains.includes('labs'),
                                icon: 'LabsFilled',
                            },
                            {
                                title: intl('vaccination'),
                                value: 'vaccination',
                                secondaryText: profileCounts.vaccination,
                                isDisabled: !permittedDomains.includes('vaccinations'),
                                icon: 'VaccinationsFilled',
                            },
                            {
                                title: intl('procedure'),
                                value: 'procedure',
                                secondaryText: profileCounts.procedure,
                                isDisabled: !permittedDomains.includes('procedures'),
                                icon: 'ProceduresFilled',
                            },
                            {
                                title: intl('visit'),
                                value: 'visit',
                                secondaryText: profileCounts.visit,
                                isDisabled: !permittedDomains.includes('visits'),
                                icon: 'VisitsFilled',
                            },
                            {
                                title: intl('careTeam'),
                                value: 'care-team',
                                secondaryText: profileCounts.careTeam,
                                isDisabled: !permittedDomains.includes('careTeam'),
                                icon: 'CareTeamFilled',
                            },
                            {
                                title: intl('reports'),
                                value: 'reports',
                                isDisabled: !permittedDomains.includes('reports'),
                                secondaryText: profileCounts.reports,
                                icon: 'FilesFilled',
                            },
                            {
                                title: intl('profileInfo'),
                                value: 'profile-info',
                                icon: 'ProfileInfoFilled',
                            },
                            {
                                title: intl('documents'),
                                value: 'documents',
                                secondaryText: profileCounts.files,
                                isDisabled: !permittedDomains.includes('files'),
                                icon: 'FolderFilled',
                            },
                        ],
                    },
                    {
                        title: intl('access'),
                        options: [
                            {
                                value: 'access',
                                secondaryText: profileCounts.collaborators,
                                title: intl('collaborators'),
                                icon: 'Users',
                            },
                        ],
                    },
                ]}
                selectedTab={isMobile ? null : sideTab}
                onChange={onTabChange as any}
                onlyRenderActiveTab
                contentWidth={contentWidth || 'xl'}
                renderContentHeader={maybeGetContentHeaderRenderer()}
            >
                {props.children}
            </SideTabNavigation>
        </SideDrawerContainer>
    );
};

const ProfileSideTabNavigation: React.FC<ProfileSideTabNavigationProps> = (props) => {
    const screen = useActiveTabActiveScreen();
    const profileId = screen?.params?.profileId;
    const medicalProfileId = screen?.params?.medicalProfileId;
    const isInviteScreen = screen?.activeScreen === 'invite'; // don't try loading profile permissions for invite screen
    const profileToPass = isInviteScreen ? null : profileId || medicalProfileId || null;

    return <ProfileSideTabNavigationWithProfileId {...props} profileId={profileToPass} />;
};

export default React.memo(ProfileSideTabNavigation);
